import { FlightDetails, ConnectionFlight } from "./types";

export type StopsFilter =
  | "none"
  | "one-or-fewer"
  | "one"
  | "two"
  | "three"
  | null;

interface StopsParams {
  Stops?: number;
  IncludeDirect?: boolean;
  IncludeConnections?: boolean;
}

// Translate the UI stops filter into the API's Stops/IncludeDirect/
// IncludeConnections params.
export const mapStopsFilterToParams = (
  stopsFilter: StopsFilter,
): StopsParams => {
  switch (stopsFilter) {
    case "none":
      return { IncludeDirect: true, IncludeConnections: false, Stops: 0 };
    case "one-or-fewer":
      return { IncludeDirect: false, IncludeConnections: true };
    case "one":
      return { Stops: 1 };
    case "two":
      return { Stops: 2 };
    case "three":
      return { Stops: 3 };
    default:
      return { IncludeDirect: true, IncludeConnections: false };
  }
};

const digits = (s: string) => s.replace(/[^0-9]/g, "");

// Stable per-render composite ids so React keys + selection survive paging.
export const withDirectFlightCompositeIds = (
  flights: FlightDetails[],
): FlightDetails[] =>
  flights.map((flight) => {
    const baseId = flight.id ?? `${Date.now()}-${Math.random()}`;
    const scheduleDate = flight.scheduleDate || flight.date;
    const compositeId = scheduleDate
      ? `${baseId}_${digits(scheduleDate)}`
      : String(baseId);
    return { ...flight, compositeId };
  });

export const withConnectionCompositeIds = (
  connections: ConnectionFlight[],
): ConnectionFlight[] =>
  connections.map((connection) => {
    const segmentsWithIds = connection.segments.map((segment) => {
      const baseId = segment.id ?? `${Date.now()}-${Math.random()}`;
      const compositeId = segment.departureTime
        ? `${baseId}_${digits(segment.departureTime)}`
        : String(baseId);
      return { ...segment, compositeId };
    });
    // Build the id from ALL segments — two itineraries can share the same first
    // leg (and even the same total duration) but differ in later legs. Keying on
    // only the first segment made those collide, so selecting a shared leg in one
    // card also selected it in every other card that shared that leg.
    const connectionCompositeId = segmentsWithIds.length
      ? `connection_${segmentsWithIds.map((s) => s.compositeId).join("_")}`
      : `connection_${Date.now()}_${Math.random()}`;
    return {
      ...connection,
      segments: segmentsWithIds,
      compositeId: connectionCompositeId,
    };
  });
